import { rankOf, type TenantRole } from '@esa/shared';

/**
 * Client-side access checks.
 *
 * These exist to decide what to OFFER, never to decide what is allowed — the
 * API re-checks every call through its own single authorization path, and this
 * module deliberately cannot grant anything.
 *
 * It uses the same role ranking as the server (imported from @esa/shared) so
 * the two cannot drift into disagreement, which is exactly how a UI ends up
 * showing a link that returns 403.
 */

/** The minimum role each area needs, matching the server's capability table. */
export const AREA_MIN_ROLE = {
  queue: 'agent',
  intake: 'agent',
  /** report.tenant */
  dashboard: 'queue_lead',
  /** config.requests (a Request manager's slice) up to config.write / roles.manage */
  admin: 'tenant_admin',
  /** report.cross_tenant */
  comparison: 'platform_admin',
  /** kb.read — reading suggestions is part of an agent's job. */
  knowledge: 'agent',
  /** Anyone signed in. */
  personal: 'requester',
} as const satisfies Record<string, TenantRole>;

/**
 * Additive grants, held at the same time as a rank role (FR-AD-06).
 *
 * The rank ladder cannot express `kb_reviewer` — it is sideways, not higher
 * — so deciding whether to OFFER Approve needs this as well as the role.
 */
export type AdditiveGrant = 'kb_reviewer' | 'request_manager';

/**
 * May this person do this, in this group — as the API will decide it? From
 * the capabilities /me returns, so a desk's own role choices are honoured
 * and the screen can never offer what the API would refuse.
 */
export function hasCapability(
  me: { tenants: { id: string; capabilities?: string[] }[] } | undefined | null,
  tenant: string,
  capability: string,
): boolean {
  return Boolean(me?.tenants.find((t) => t.id === tenant)?.capabilities?.includes(capability));
}

export function canReviewKb(
  role: string | null | undefined,
  grants: readonly string[] | undefined,
): boolean {
  // By rank from tenant_admin up, or by the grant from agent up.
  if (hasRole(role, 'tenant_admin')) return true;
  return hasRole(role, 'agent') && (grants ?? []).includes('kb_reviewer');
}

export type Area = keyof typeof AREA_MIN_ROLE;

export function hasRole(role: string | null | undefined, min: TenantRole): boolean {
  if (!role) return false;
  const idx = rankOf(role as TenantRole);
  if (idx < 0) return false;
  return idx >= rankOf(min);
}

/**
 * Areas whose capability a desk may give a different role (065). Where the
 * desk's capabilities are known, they decide; the role ladder is the answer
 * only when they are not.
 */
const AREA_CAPABILITY: Partial<Record<Area, string>> = {
  dashboard: 'report.tenant',
  knowledge: 'kb.read',
  // The Requests part of Configuration; a tenant admin holds it by rank,
  // a Request manager by grant.
  admin: 'config.requests',
};

export function canAccess(role: string | null | undefined, area: Area, capabilities?: readonly string[]): boolean {
  const cap = AREA_CAPABILITY[area];
  if (cap && capabilities) return capabilities.includes(cap);
  return hasRole(role, AREA_MIN_ROLE[area]);
}

/** This person's capabilities in one group, from /me. */
export function capabilitiesIn(
  me: { tenants: { id: string; capabilities?: string[] }[] } | undefined | null,
  tenant: string,
): string[] | undefined {
  return me?.tenants.find((t) => t.id === tenant)?.capabilities;
}

/**
 * Where to send someone whose current page is not available in the group they
 * just switched to. Highest-value page they can actually open.
 */
export function landingFor(role: string | null | undefined): string {
  if (canAccess(role, 'queue')) return '/queue';
  return '/my';
}

/** The area a path belongs to, for the redirect-on-switch check. */
export function areaForPath(path: string): Area | null {
  if (path.startsWith('/queue') || path.startsWith('/t/')) return 'queue';
  // `/reports/*` sits at the same threshold as the dashboard. Without this
  // the Shell's "you cannot see this group's reports" redirect silently
  // no-ops on the route, leaving a stale page up until the next navigation.
  if (path.startsWith('/dashboard') || path.startsWith('/reports')) return 'dashboard';
  if (path.startsWith('/intake')) return 'intake';
  if (path.startsWith('/admin')) return 'admin';
  if (path.startsWith('/comparison')) return 'comparison';
  if (path.startsWith('/knowledge')) return 'knowledge';
  if (path.startsWith('/my') || path.startsWith('/new') || path.startsWith('/search')) {
    return 'personal';
  }
  return null;
}

export const ROLE_LABEL: Record<TenantRole, string> = {
  requester: 'Requester',
  agent: 'Agent',
  queue_lead: 'Queue lead',
  tenant_admin: 'Group admin',
  platform_admin: 'Platform admin',
};

/**
 * Whether a ticket's Actions tab is for this person on this desk.
 *
 * BY CAPABILITY, AS THE API DECIDES IT. This read the `action_operator`
 * grant alone, but the API also allows `action.propose` by rank at
 * platform_admin -- so a platform admin was allowed to act and never shown
 * the tab, the model was never asked, and an Egnyte folder request on QA got
 * no proposal. `/me` reports the capability exactly as the API computes it.
 */
export function canUseActions(
  me: { tenants: { id: string; capabilities?: string[] }[] } | undefined | null,
  tenant: string,
  modules: readonly string[],
): boolean {
  return modules.includes('actions') && hasCapability(me, tenant, 'action.propose');
}
